import React from 'react';
import { Box, Paper, Typography, Container, Button, Link as MuiLink, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PublicLayout } from '../components/PublicLayout';
import { useTheme } from '@mui/material/styles';
import { createGradientTitleStyles } from '../theme/utils';

export const CookiePolicy: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  return (
    <PublicLayout>
      <Container maxWidth="md">
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            mb: 4,
          }}
        >
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{ mb: 3 }}
          >
            {t('common.back', 'Volver')}
          </Button>

          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom 
            sx={{ 
              mb: 2, 
              ...createGradientTitleStyles(theme),
            }}
          >
            {t('cookies.policyTitle', 'Política de Cookies')}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 4, fontStyle: 'italic' }}>
            {t('cookies.lastUpdated', 'Última actualización: Enero 2025')}
          </Typography>

          <Box sx={{ '& > *': { mb: 4 } }}>
            <Typography variant="body1" paragraph sx={{ fontWeight: 500 }}>
              {t('cookies.intro',
                'En cumplimiento de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y de Comercio Electrónico (LSSI-CE) y el Reglamento General de Protección de Datos (RGPD), Cervellia Technologies S.L informa sobre el uso de cookies en su sitio web.')}
            </Typography>

            <section>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                {t('cookies.section1.title', '1. ¿Qué son las Cookies?')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('cookies.section1.content',
                  'Las cookies son pequeños archivos de texto que se almacenan en el dispositivo del usuario (ordenador, tablet, smartphone) cuando visita un sitio web. Las cookies permiten que el sitio web recuerde acciones y preferencias del usuario durante un período de tiempo, por lo que no tiene que volver a configurarlas cada vez que regrese al sitio o navegue de una página a otra.')}
              </Typography>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                {t('cookies.section2.title', '2. Tipos de Cookies que Utilizamos')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('cookies.section2.intro', 'Según su finalidad, utilizamos los siguientes tipos de cookies:')}
              </Typography>

              <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600, mb: 1.5, mt: 3 }}>
                {t('cookies.section2.subsection1', '2.1. Cookies Técnicas (Necesarias)')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('cookies.section2.subsection1Desc',
                  'Son esenciales para el funcionamiento del sitio web y no pueden desactivarse. Permiten la navegación y el uso de funciones básicas del sitio.')}
              </Typography>

              <TableContainer sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'background.default' }}>
                      <TableCell sx={{ fontWeight: 600 }}>{t('cookies.table.cookie', 'Cookie')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('cookies.table.purpose', 'Finalidad')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('cookies.table.duration', 'Duración')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('cookies.table.type', 'Tipo')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>cookie_consent</TableCell>
                      <TableCell>{t('cookies.table.purpose1', 'Almacena la preferencia del usuario sobre el consentimiento de cookies')}</TableCell>
                      <TableCell>{t('cookies.table.duration1', '1 año')}</TableCell>
                      <TableCell>{t('cookies.table.type1', 'Propia')}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>session_id</TableCell>
                      <TableCell>{t('cookies.table.purpose2', 'Mantiene la sesión del usuario durante la navegación')}</TableCell>
                      <TableCell>{t('cookies.table.duration2', 'Sesión')}</TableCell>
                      <TableCell>{t('cookies.table.type2', 'Propia')}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600, mb: 1.5, mt: 3 }}>
                {t('cookies.section2.subsection2', '2.2. Cookies de Análisis')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('cookies.section2.subsection2Desc',
                  'Permiten analizar el comportamiento de los usuarios en el sitio web para mejorar su funcionamiento y experiencia de usuario.')}
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  <strong>{t('cookies.section2.note', 'Nota:')}</strong>{' '}
                  {t('cookies.section2.noteContent',
                    'Actualmente no utilizamos cookies de análisis de terceros (como Google Analytics). Si en el futuro se implementan, se actualizará esta política y se solicitará el consentimiento previo del usuario.')}
                </Typography>
              </Box>

              <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600, mb: 1.5, mt: 3 }}>
                {t('cookies.section2.subsection3', '2.3. Cookies de Funcionalidad')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('cookies.section2.subsection3Desc',
                  'Permiten recordar las preferencias del usuario (idioma, región, etc.) para ofrecer una experiencia más personalizada.')}
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  <strong>{t('cookies.section2.note', 'Nota:')}</strong>{' '}
                  {t('cookies.section2.noteContent2',
                    'Actualmente no utilizamos cookies de funcionalidad. Si en el futuro se implementan, se actualizará esta política.')}
                </Typography>
              </Box>

              <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600, mb: 1.5, mt: 3 }}>
                {t('cookies.section2.subsection4', '2.4. Cookies de Publicidad')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('cookies.section2.subsection4Desc',
                  'Se utilizan para mostrar publicidad relevante al usuario.')}
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  <strong>{t('cookies.section2.note', 'Nota:')}</strong>{' '}
                  {t('cookies.section2.noteContent3',
                    'Actualmente no utilizamos cookies publicitarias. Si en el futuro se implementan, se solicitará el consentimiento previo del usuario.')}
                </Typography>
              </Box>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                {t('cookies.section3.title', '3. Cookies de Terceros')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('cookies.section3.content',
                  'Actualmente, nuestro sitio web no utiliza cookies de terceros. Si en el futuro se implementan servicios de terceros que utilicen cookies (como Google Analytics, redes sociales, etc.), se actualizará esta política y se solicitará el consentimiento previo del usuario.')}
              </Typography>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                {t('cookies.section4.title', '4. Base Legal')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('cookies.section4.intro', 'El uso de cookies se basa en:')}
              </Typography>
              <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
                <li>
                  <strong>{t('cookies.section4.item1', 'Cookies técnicas:')}</strong>{' '}
                  {t('cookies.section4.item1Desc', 'Interés legítimo (funcionamiento del sitio web). No requieren consentimiento.')}
                </li>
                <li>
                  <strong>{t('cookies.section4.item2', 'Cookies de análisis, funcionalidad y publicidad:')}</strong>{' '}
                  {t('cookies.section4.item2Desc', 'Consentimiento explícito del usuario (si se implementan en el futuro).')}
                </li>
              </Typography>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                {t('cookies.section5.title', '5. Cómo Gestionar las Cookies')}
              </Typography>

              <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600, mb: 1.5, mt: 2 }}>
                {t('cookies.section5.subsection1', '5.1. A través de nuestro sitio web')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('cookies.section5.subsection1Desc',
                  'Puede gestionar sus preferencias de cookies a través del banner de cookies que aparece en su primera visita al sitio web. Puede aceptar todas las cookies, rechazar las no esenciales o configurar sus preferencias.')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('cookies.section5.subsection1Desc2',
                  'También puede modificar sus preferencias en cualquier momento haciendo clic en el enlace "Gestionar cookies" que aparece en el footer del sitio web.')}
              </Typography>

              <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600, mb: 1.5, mt: 3 }}>
                {t('cookies.section5.subsection2', '5.2. A través del navegador')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('cookies.section5.subsection2Desc',
                  'La mayoría de los navegadores permiten gestionar las preferencias de cookies. Puede configurar su navegador para que rechace cookies o le avise cuando un sitio web intente colocar una cookie en su dispositivo.')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('cookies.section5.subsection2Desc2', 'Las instrucciones para gestionar cookies en los principales navegadores son:')}
              </Typography>
              <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><strong>Google Chrome:</strong> {t('cookies.section5.chrome', 'Configuración > Privacidad y seguridad > Cookies y otros datos de sitios')}</li>
                <li><strong>Mozilla Firefox:</strong> {t('cookies.section5.firefox', 'Opciones > Privacidad y seguridad > Cookies y datos del sitio')}</li>
                <li><strong>Safari:</strong> {t('cookies.section5.safari', 'Preferencias > Privacidad > Cookies y datos de sitios web')}</li>
                <li><strong>Microsoft Edge:</strong> {t('cookies.section5.edge', 'Configuración > Cookies y permisos del sitio > Cookies y datos del sitio')}</li>
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 2, border: '1px solid', borderColor: 'warning.main' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t('cookies.section5.warning', 'Importante:')}
                </Typography>
                <Typography variant="body2">
                  {t('cookies.section5.warningContent',
                    'Tenga en cuenta que si desactiva las cookies técnicas, algunas funcionalidades del sitio web pueden no estar disponibles o no funcionar correctamente.')}
                </Typography>
              </Box>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                {t('cookies.section6.title', '6. Actualización de la Política de Cookies')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('cookies.section6.content',
                  'Cervellia Technologies S.L se reserva el derecho de modificar esta Política de Cookies para adaptarla a cambios legislativos, jurisprudenciales o técnicos. Se recomienda revisar periódicamente esta política.')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('cookies.section6.content2',
                  'En caso de cambios significativos, se notificará a los usuarios a través del sitio web o por otros medios.')}
              </Typography>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                {t('cookies.section7.title', '7. Más Información')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('cookies.section7.content',
                  'Para obtener más información sobre las cookies y cómo gestionarlas, puede consultar:')}
              </Typography>
              <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
                <li>
                  <MuiLink href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer">
                    www.allaboutcookies.org
                  </MuiLink>
                </li>
                <li>
                  <MuiLink href="https://www.youronlinechoices.com" target="_blank" rel="noopener noreferrer">
                    www.youronlinechoices.com
                  </MuiLink>
                </li>
              </Typography>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                {t('cookies.section8.title', '8. Contacto')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('cookies.section8.content',
                  'Para cualquier consulta relacionada con el uso de cookies, puede contactarnos en:')}
              </Typography>
              <Typography variant="body1">
                <strong>{t('cookies.section8.emailLabel', 'Correo electrónico:')}</strong>{' '}
                <MuiLink href="mailto:info@cervellia.es">info@cervellia.es</MuiLink>
              </Typography>
            </section>
          </Box>
        </Paper>
      </Container>
    </PublicLayout>
  );
};

